import React, { useState, useRef, useEffect } from "react";
import { Carousel } from "../Carousel/Carousel";
import { GameCard } from "../GameCard/GameCard";
import "./GameHistory.css";
import Button from "../Button/Button3";
import CarmenImage from "../../assets/images/carmen.png";
import resolveMediaUrl from "../../utils/resolveMediaUrl";
import { resolveDisplayedScore } from "../../utils/gameSummary";

const GameHistory = ({ games }) => {
  const [gameFilter, setGameFilter] = useState("played");
  const carouselRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  const handleFilterClick = (filter) => {
    setGameFilter((prevFilter) => (prevFilter === filter ? "played" : filter));
  };

  const playedStatuses = new Set(["finished", "settled"]);
  const finishedGames = games.filter((game) => {
    const attendance = String(game.attendance || "").toLowerCase();
    if (attendance === "waitlist") {
      return false;
    }

    const status = String(game.gameId?.status || game.status || "").toLowerCase();
    return playedStatuses.has(status);
  });

  const getGameImageSrc = (game) => {
    if (game.imageSrc) return game.imageSrc;

    const stadiumPicturePath =
      game.gameId?.stadium?.stadiumPicturePath ||
      game.gameId?.stadiumPicturePath ||
      game.stadiumPicturePath;

    return stadiumPicturePath
      ? resolveMediaUrl(stadiumPicturePath, CarmenImage)
      : CarmenImage;
  };

  const transformGame = (game) => {
    const displayedScore = resolveDisplayedScore(
      game.gameId?.result,
      game.gameId?.matchEvents
    );

    return {
      gameId: game.gameId?._id || game._id || "Unknown ID",
      gameName: game.gameId?.stadium?.name || game.stadium || "Unknown Game",
      gameSubtitle: game.gameId?.type || "Unknown Type",
      gameDay: game.gameId?.date || game.date || null,
      gameTime: game.gameId?.time || game.time || null,
      imageSrc: getGameImageSrc(game),
      result: game.result || "unknown",
      score: game.gameId?.result
        ? `${displayedScore.team1Goals}:${displayedScore.team2Goals}`
        : null,
    };
  };

  const transformedGames = finishedGames.map(transformGame);

  const filteredGames = transformedGames.filter((game) => {
    if (gameFilter === "played") return true;
    if (gameFilter === "won") return game.result === "win";
    if (gameFilter === "lost") return game.result === "loss";
    if (gameFilter === "draw") return game.result === "draw";
    return false;
  });

  const scrollRight = () => {
    const carousel = carouselRef.current;
    if (carousel) {
      const cardWidth = carousel.offsetWidth / 6;
      const maxScrollLeft = carousel.scrollWidth - carousel.offsetWidth;

      if (carousel.scrollLeft + carousel.offsetWidth < carousel.scrollWidth - cardWidth) {
        carousel.scrollBy({ left: cardWidth, behavior: "smooth" });
      } else {
        carousel.scrollTo({ left: maxScrollLeft, behavior: "smooth" });
        setTimeout(() => {
          carousel.scrollTo({ left: 0, behavior: "smooth" });
        }, 3000);
      }
    }
  };

  const scrollLeft = () => {
    const carousel = carouselRef.current;
    if (carousel) {
      const cardWidth = carousel.offsetWidth / 6;
      carousel.scrollBy({ left: -cardWidth, behavior: "smooth" });
    }
  };

  const startAutoScroll = () => {
    stopAutoScroll();
    scrollIntervalRef.current = setInterval(() => {
      scrollRight();
    }, 3000);
  };

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
    }
  };

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.scrollLeft = 0;
      startAutoScroll();
    }

    return () => {
      stopAutoScroll();
    };
  }, []);

  return (
    <div
      className="game-history-container"
      onMouseEnter={stopAutoScroll}
      onMouseLeave={startAutoScroll}
    >
      {/* Game Filters */}
      <div className="game-history-filters">
        <Button
          variant="small"
          primaryText="WON"
          onClick={() => handleFilterClick("won")}
          styleType={gameFilter === "won" ? "active" : "inactive"}
        />
        <Button
          variant="small"
          primaryText="LOST"
          onClick={() => handleFilterClick("lost")}
          styleType={gameFilter === "lost" ? "active" : "inactive"}
        />
        {/* Bottom row */}
        <Button
          variant="small"
          primaryText="DRAW"
          onClick={() => handleFilterClick("draw")}
          styleType={gameFilter === "draw" ? "active" : "inactive"}
        />
        <Button
          variant="small"
          primaryText="PLAYED"
          onClick={() => handleFilterClick("played")}
          styleType={gameFilter === "played" ? "active" : "inactive"}
        />
      </div>


      {/* Conditional Rendering */}
      {filteredGames.length === 0 ? (
        <div className="no-games-message">
          <p>No games found. Play your first game!</p>
        </div>
      ) : (
        <div className="carousel-shell">
          <button className="carousel-nav left" onClick={scrollLeft}>
            {"<"}
          </button>
          <div className="carousel-wrapper">
            <Carousel
              ref={carouselRef}
              className={`game-history-carousel ${filteredGames.length <= 5 ? "centered" : ""}`}
            >
              {filteredGames.map((game) => (
                <GameCard
                  key={game.gameId}
                  imageSrc={game.imageSrc}
                  gameName={game.gameName}
                  gameSubtitle={game.gameSubtitle}
                  gameDay={game.gameDay}
                  gameTime={game.gameTime}
                  score={game.score}
                  gameId={game.gameId}
                  className="game-card-container"
                />
              ))}
            </Carousel>
          </div>
          <button className="carousel-nav right" onClick={scrollRight}>
            {">"}
          </button>
        </div>
      )}
    </div>
  );
};

export default GameHistory;
